/* Programs: /programs
   ----------------------------------------------------------------------
   One of the four main sections (owner, 2026-10-06): the five programs,
   in the shared catalogue structure (src/components/catalog). Copy and
   photos come from src/data/offer.js, facts from the For Schools build
   prompt. Elite Development and Sports Performance were taken off the
   site the same day; their URLs redirect here.

   Order: hero → Future Skills Programmes (steps, three programs, what
   every program includes, where the projects go) → Foundation
   Programmes (two programs) → for schools / for families → FAQ.
   School prices stay on /schools; families register through the home
   contact form, as before. */

import { SIZES } from "../lib/img";
import React from "react";
import "./Home.css";
import "./Programs.css";
import SEO from "../components/SEO";
import SiteHeader from "../components/SiteHeader";
import LocaleLink from "../components/LocaleLink";
import { useLocale, useL } from "../i18n/LocaleContext";
import { PROGRAMS, PROGRAMS_PAGE, SECTION } from "../data/offer";
import { SCHOOLS } from "../data/schools";
import { faqPageSchema, breadcrumbSchema, programsListSchema } from "../data/schemas";
import {
  ARROW, Pic, CheckList, FloatingCta, useHashScroll,
  Accent, SectionHead, StepCards, InfoCard, FaqList,
} from "../components/catalog/CatalogParts";
import { track } from "../lib/analytics";

/* The school FAQ entries that answer questions about the programs
   themselves (same words as on /schools). */
const FAQ_KEYS = [0, 1, 2, 3, 4];
const FAQ = FAQ_KEYS.map((i) => SCHOOLS.faq[i]);

function ProgramCard({ p, fullPage }) {
  return (
    <article className="cat-info" id={p.id}>
      <Pic src={p.photo.src} alt={p.photo.alt} className="cat-info-media" />
      <div className="cat-info-body">
        <p className="cat-card-eyebrow">{p.eyebrow}</p>
        <h3 className="cat-info-title">{p.name}</h3>
        <p className="cat-info-text">{p.text}</p>
        <ul className="cat-card-facts">{p.bullets.map((b, i) => <li key={i}>{b}</li>)}</ul>
        {p.page && <LocaleLink to={p.page} className="cat-info-link">{fullPage} {ARROW}</LocaleLink>}
      </div>
    </article>
  );
}

export default function Programs() {
  const { locale, lp } = useLocale();
  const c = useL(PROGRAMS_PAGE);
  const programs = useL(PROGRAMS);
  const name = useL(SECTION.programs);
  const school = useL(SCHOOLS);
  const faq = useL(FAQ);
  useHashScroll();

  const futureSkills = programs.filter((p) => p.group === "futureSkills");
  const foundation = programs.filter((p) => p.group === "foundation");

  return (
    <div className="cat-page">
      <SEO
        title={c.seo.title}
        description={c.seo.description}
        path="/programs"
        image={`${PROGRAMS[0].photo.src}.jpg`}
        schema={[
          programsListSchema(PROGRAMS, locale),
          faqPageSchema(FAQ, locale),
          breadcrumbSchema([
            { name: SCHOOLS.home, path: "/" },
            { name: SECTION.programs, path: "/programs" },
          ], locale),
        ]}
      />
      <SiteHeader />
      <main id="main">
        {/* ── Hero ── */}
        <header className="cat-hero">
          <div className="cat-hero-inner">
            <div className="cat-hero-copy">
              <p className="cat-eyebrow">{name}</p>
              <h1 className="cat-title"><Accent title={c.hero.title} /></h1>
              <p className="cat-lead">{c.hero.lead}</p>
              {/* Pick a program straight from the hero (owner, 2026-10-07):
                  each one opens its own page, no scrolling needed. */}
              <nav className="cat-hero-picks" aria-label={c.hero.pickLabel}>
                <ul>
                  {programs.map((p) => (
                    <li key={p.id}>
                      <LocaleLink to={p.page} className="cat-hero-pick" onClick={() => track("programs_hero_pick_click", { program: p.id })}>
                        <span className="cat-hero-pick-eyebrow">{p.eyebrow}</span>
                        <span className="cat-hero-pick-name">{p.name}</span>
                        {ARROW}
                      </LocaleLink>
                    </li>
                  ))}
                </ul>
              </nav>
              <div className="cat-actions cat-hero-actions">
                <a href={lp("/#contact")} className="btn btn-gold-lg" onClick={() => track("programs_hero_cta_click", { target: "contact" })}>{c.hero.ctaTalk}</a>
              </div>
            </div>
            <div className="cat-mosaic">
              {c.hero.photos.map((p, i) => <Pic key={p.src} src={p.src} alt={p.alt} eager={i < 2} sizes={SIZES.strip} />)}
            </div>
          </div>
        </header>

        {/* ── Future Skills Programmes ── */}
        <section className="cat-section cat-section--white" id="all-programs" aria-labelledby="fs-title">
          <div className="cat-wrap">
            <SectionHead id="fs-title" eyebrow={c.futureSkills.eyebrow} title={c.futureSkills.title} lead={c.futureSkills.lead} />
            <StepCards items={school.futureSkills.steps} goal />
            <ul className="cat-cards">
              {futureSkills.map((p) => <li key={p.id}><ProgramCard p={p} fullPage={c.fullPage} /></li>)}
            </ul>
            <div className="cat-split">
              <div className="cat-panel cat-panel--accent">
                <h3 className="cat-panel-title">{school.futureSkills.includesTitle}</h3>
                <CheckList items={school.futureSkills.includes} />
              </div>
              <div className="cat-panel cat-panel--green">
                <h3 className="cat-panel-title">{c.showcase.title}</h3>
                <p className="cat-text">{c.showcase.text}</p>
                <p className="cat-small">
                  <LocaleLink to="/schools#showcase" className="cat-info-link">{c.showcase.link} {ARROW}</LocaleLink>
                </p>
              </div>
            </div>
          </div>
        </section>

        {/* ── Foundation Programmes ── */}
        <section className="cat-section" id="foundation" aria-labelledby="fd-title">
          <div className="cat-wrap">
            <SectionHead id="fd-title" eyebrow={c.foundation.eyebrow} title={c.foundation.title} />
            <ul className="cat-cards cat-cards--two">
              {foundation.map((p) => <li key={p.id}><ProgramCard p={p} fullPage={c.fullPage} /></li>)}
            </ul>
            <p className="cat-line">{c.foundation.note}</p>
          </div>
        </section>

        {/* ── For schools / for families ── */}
        <section className="cat-section cat-section--navy" id="enquire" aria-labelledby="who-title">
          <div className="cat-wrap">
            <SectionHead id="who-title" title={c.who.title} />
            <div className="cat-cards cat-cards--two">
              <InfoCard
                title={c.who.schools.title}
                text={c.who.schools.text}
                link={{ href: "/schools#future-skills", label: c.who.schools.cta }}
              />
              <article className="cat-info">
                <div className="cat-info-body">
                  <h3 className="cat-info-title">{c.who.families.title}</h3>
                  <p className="cat-info-text">{c.who.families.text}</p>
                  <a href={lp("/#contact")} className="cat-info-link" onClick={() => track("programs_family_cta_click")}>{c.who.families.cta} {ARROW}</a>
                </div>
              </article>
            </div>
          </div>
        </section>

        {/* ── FAQ ── */}
        <section className="cat-section cat-section--white" aria-labelledby="faq-title">
          <div className="cat-wrap cat-wrap--narrow">
            <h2 id="faq-title" className="cat-h2 cat-h2--gap">{c.faqTitle}</h2>
            <FaqList items={faq} />
          </div>
        </section>
      </main>
      <FloatingCta
        href="#enquire"
        label={c.hero.ctaTalk}
        heroSelector=".cat-hero-actions"
        targetId="enquire"
        event="programs_float_cta_click"
      />
    </div>
  );
}
